/*
 * Selector de variantes nativo (Sewfil). Sustituye al selector de SellersDash.
 * Pide a Shopify el estado de la combinación elegida con la Section Rendering API:
 *   {product_url}?option_values=ID1,ID2,ID3&section_id={section}
 * y actualiza opciones disponibles, precio, botón, variante del formulario, URL e imagen.
 */
if (!customElements.get('variant-picker-nativo')) {
  class VariantPickerNativo extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.form = document.getElementById(this.dataset.form);
      this.requestId = 0;
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"][data-option-value-id]')) this.onOptionChange(e.target);
      });
      this.sortColors();
      this.filter = this.querySelector('.nativo-filter');
      if (this.filter) this.filter.addEventListener('input', () => this.applyFilter());
      const wrapper = this.querySelector('.color_wrapper');
      const toggle = this.querySelector('.nativo-toggle');
      if (wrapper && toggle) {
        toggle.addEventListener('click', () => {
          const open = !wrapper.classList.contains('expandhobe');
          wrapper.classList.toggle('expandhobe', open);
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
    }

    // Ordena los colores como en la carta de colores (data-order), igual que la ficha antigua.
    sortColors() {
      const wrapper = this.querySelector('.color_wrapper');
      if (!wrapper) return;
      const cells = Array.from(wrapper.querySelectorAll(':scope > .colorfill'));
      cells
        .map((cell, i) => ({ cell, key: Number(cell.dataset.order || 99999), i }))
        .sort((a, b) => a.key - b.key || a.i - b.i)
        .forEach(({ cell }) => wrapper.appendChild(cell));
    }

    get fieldsets() { return Array.from(this.querySelectorAll('fieldset.nativo-option')); }

    selectedIds(root = this) {
      return Array.from(root.querySelectorAll('fieldset.nativo-option')).map((fs) => {
        const checked = fs.querySelector('input[data-option-value-id]:checked');
        return checked ? checked.dataset.optionValueId : null;
      });
    }

    static isSelectable(input) {
      if (!input || input.classList.contains('is-unavailable')) return false;
      if (input.closest('.hidecolorfill')) return false;
      const label = input.nextElementSibling;
      return !(label && label.classList.contains('nativo-hidden'));
    }

    async fetchState(ids) {
      const url = `${this.productUrl}?option_values=${ids.filter(Boolean).join(',')}&section_id=${this.sectionId}`;
      const response = await fetch(url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector(`variant-picker-nativo[data-section="${this.sectionId}"]`) ? doc : null;
    }

    async onOptionChange(input) {
      const changedIndex = Number(input.closest('fieldset').dataset.optionIndex);
      const requestId = ++this.requestId;
      this.classList.add('is-loading');
      try {
        let ids = this.selectedIds();
        let doc = await this.fetchState(ids);
        // Si al cambiar una opción la siguiente (salvo el color) deja de existir, se elige la primera válida.
        for (let i = changedIndex + 1; doc && i < this.fieldsets.length; i++) {
          const remoteFs = doc.querySelectorAll(`variant-picker-nativo[data-section="${this.sectionId}"] fieldset.nativo-option`)[i];
          if (!remoteFs || remoteFs.querySelector('.color_wrapper')) break;
          const checked = remoteFs.querySelector('input[data-option-value-id]:checked');
          if (VariantPickerNativo.isSelectable(checked)) continue;
          const first = Array.from(remoteFs.querySelectorAll('input[data-option-value-id]')).find(VariantPickerNativo.isSelectable);
          if (!first) break;
          ids[i] = first.dataset.optionValueId;
          doc = await this.fetchState(ids);
        }
        if (requestId !== this.requestId || !doc) return;
        this.applyState(doc);
      } catch (e) {
        console.error('variant-picker-nativo', e);
      } finally {
        if (requestId === this.requestId) this.classList.remove('is-loading');
      }
    }

    applyState(doc) {
      const remote = doc.querySelector(`variant-picker-nativo[data-section="${this.sectionId}"]`);
      remote.querySelectorAll('input[data-option-value-id]').forEach((remoteInput) => {
        const local = this.querySelector(`input[data-option-value-id="${remoteInput.dataset.optionValueId}"]`);
        if (!local) return;
        local.className = remoteInput.className;
        local.checked = remoteInput.hasAttribute('checked');
        const remoteWrap = remoteInput.closest('.colorfill');
        const localWrap = local.closest('.colorfill');
        if (remoteWrap && localWrap) {
          localWrap.classList.toggle('hidecolorfill', remoteWrap.classList.contains('hidecolorfill'));
          // Colores que toman el tono de un metacampo de la variante: se copian al aparecer
          const rLabel = remoteWrap.querySelector('.color-style');
          const lLabel = localWrap.querySelector('.color-style');
          if (rLabel && lLabel && /background-color:\s*[^;\s]/.test(rLabel.getAttribute('style') || '')) lLabel.setAttribute('style', rLabel.getAttribute('style'));
          const rTitle = remoteWrap.querySelector('.color-meta-title');
          const lTitle = localWrap.querySelector('.color-meta-title');
          if (rTitle && lTitle && rTitle.textContent.trim()) lTitle.textContent = rTitle.textContent;
        } else if (remoteInput.nextElementSibling && local.nextElementSibling) {
          local.nextElementSibling.className = remoteInput.nextElementSibling.className;
        }
      });

      const variantScript = remote.querySelector('script[data-selected-variant]');
      const variant = variantScript ? JSON.parse(variantScript.textContent || 'null') : null;

      const priceId = `price-${this.sectionId}`;
      const priceSource = doc.getElementById(priceId);
      const priceDestination = document.getElementById(priceId);
      if (priceSource && priceDestination) priceDestination.innerHTML = priceSource.innerHTML;
      if (priceDestination) priceDestination.classList.toggle('visibility-hidden', !variant);

      this.updateForm(variant);
      if (variant) {
        window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
        this.updateMedia(variant);
        const pickup = document.querySelector('pickup-availability');
        if (pickup && typeof pickup.fetchAvailability === 'function') {
          try { pickup.fetchAvailability(variant.id); } catch (e) { /* sin recogida en tienda */ }
        }
      }
      this.applyFilter();
    }

    updateForm(variant) {
      if (!this.form) return;
      const idInput = this.form.querySelector('input[name="id"]');
      const button = this.form.querySelector('[name="add"]');
      const strings = window.variantStrings || {};
      const desktopText = this.form.querySelector('#CarroPC');
      const mobileText = this.form.querySelector('#CarroMovil');
      let desktop; let mobile;
      if (variant) {
        if (idInput) {
          idInput.value = variant.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (variant.available) {
          desktop = strings.addToCart || 'Agregar al carrito';
          mobile = 'Agregar';
        } else {
          desktop = mobile = strings.soldOut || 'Agotado';
        }
        if (button) button.disabled = !variant.available;
      } else {
        const colorChecked = this.querySelector('.color_wrapper input:checked');
        const colorHidden = colorChecked && colorChecked.closest('.hidecolorfill');
        desktop = mobile = colorHidden || !colorChecked ? 'Elige un color' : (strings.unavailable || 'No disponible');
        if (button) button.disabled = true;
      }
      if (desktopText) desktopText.textContent = desktop;
      if (mobileText) mobileText.textContent = mobile;
    }

    updateMedia(variant) {
      if (!variant.featured_media || typeof window.flkty === 'undefined' || !window.flkty) return;
      const cells = Array.from(document.querySelectorAll('.product__image-slider .carousel-cell'));
      const index = cells.findIndex((cell) => {
        const img = cell.querySelector('[data-media-id]');
        return img && Number(img.getAttribute('data-media-id')) === variant.featured_media.id;
      });
      if (index > -1) window.flkty.select(index);
    }

    applyFilter() {
      if (!this.filter) return;
      const term = this.filter.value.trim().toLowerCase();
      // Al buscar se abren todos los colores, para ver los resultados aunque estén al final de la lista
      const wrapper = this.querySelector('.color_wrapper');
      const toggle = this.querySelector('.nativo-toggle');
      if (term && wrapper && toggle && !wrapper.classList.contains('expandhobe')) {
        wrapper.classList.add('expandhobe');
        toggle.setAttribute('aria-expanded', 'true');
      }
      this.querySelectorAll('.color_wrapper .colorfill').forEach((cell) => {
        const code = (cell.querySelector('.color-meta-title')?.textContent || '').trim().toLowerCase();
        const name = (cell.querySelector('input')?.value || '').toLowerCase();
        cell.classList.toggle('nativo-filter-out', term !== '' && !code.includes(term) && !name.includes(term));
      });
    }
  }
  customElements.define('variant-picker-nativo', VariantPickerNativo);
}
